import microApp from '@micro-zoe/micro-app';
import config from '@/config/user.env';
import { USER_INFO, STORE_ACCOUNT } from '@/config/const';
import cookies from 'js-cookie';
import { isSupplierUserCode } from '@/utils/userCodeHelper';

/**
 * 子应用预加载（两段）：
 *
 * 1) 仅登录页预「即将打开的首页」1 个（输入账号 / 记住账号；有提前量，进入后 script 走 disk cache）
 *    redirectUrl 只作解析提示（会话过期回登录且带目标路径时更准），不是独立预取通道
 * 2) initSystem 成功后（已进首页）延迟预其余白名单（默认 3500ms）；永不二次预首页；
 *    阶段二不含 microLsp、不含当前首页；首页为 microLsp 时整段跳过
 *
 * 【强刷新不做首页预取】预加载与挂载几乎同时，间隔过短无收益，甚至抢带宽；
 * 只标记当前 URL 上的 micro* 供阶段二排除。
 */
const PREFETCHABLE_MICRO_APPS: Record<string, { url: string; iframe: boolean; level: number }> = {
  microLsp: { url: config.microLspUrl, iframe: true, level: 2 },
  microNtp: { url: config.microNtpUrl, iframe: true, level: 2 },
  microOms: { url: config.microOmsUrl, iframe: true, level: 2 },
  microTms: { url: config.microTmsUrl, iframe: true, level: 2 },
  microDataReport: { url: config.microDataReportUrl, iframe: true, level: 2 },
  microLsrm: { url: config.microLsrmUrl, iframe: true, level: 2 },
};

/** 进首页后预其余白名单的延时（原 3000ms → 3500ms）；登录页预首页不使用此值 */
export const MICRO_APP_PREFETCH_DELAY_MS = 3500;

const MICRO_APP_NAME_PATTERN = /\/(micro[A-Za-z]+)/;

const prefetchedApps = new Set<string>();
let othersPrefetchScheduled = false;
/** 当前认定的首页子应用名（登录页预取目标 / 阶段二排除项） */
let homePrefetchApp: string | null = null;

const buildPrefetchApps = (names: Iterable<string>) =>
  [...names]
    .map((name) => ({
      name,
      ...PREFETCHABLE_MICRO_APPS[name],
    }))
    .filter((app) => app.url);

/** 按 app.name 去重；同名已预过则不再调 microApp.preFetch */
const prefetchAppsOnce = (apps: ReturnType<typeof buildPrefetchApps>, delay: number) => {
  const pending = apps.filter((app) => !prefetchedApps.has(app.name));
  pending.forEach((app) => prefetchedApps.add(app.name));
  if (pending.length > 0) {
    microApp.preFetch(pending, delay);
  }
};

const extractMicroAppNameFromPath = (pathOrUrl?: string | null): string | null => {
  if (!pathOrUrl) {
    return null;
  }
  let decoded = pathOrUrl;
  try {
    decoded = decodeURIComponent(pathOrUrl);
  } catch {
    // keep raw
  }
  const match = decoded.match(MICRO_APP_NAME_PATTERN);
  if (match && match[1] in PREFETCHABLE_MICRO_APPS) {
    return match[1];
  }
  return null;
};

/** 登录页 hash 上的 redirectUrl（仅解析用） */
const getLoginRedirectUrl = (): string | null => {
  const hash = window.location.hash || '';
  const match = hash.match(/[#/]*login\?redirectUrl=([^&]*)/i) || hash.match(/redirectUrl=([^&]*)/i);
  if (!match?.[1]) {
    return null;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const getCurrentHashPath = (): string => {
  const hash = window.location.hash || '';
  const path = hash.replace(/^#/, '').split('?')[0] || '';
  return path.startsWith('/') ? path : `/${path}`;
};

/** 登录页解析即将打开的首页：redirectUrl（若有）> 供应商 LSP > 默认 DataReport */
const resolveHomePrefetchApp = (userCode?: string | null): string => {
  const fromRedirect = extractMicroAppNameFromPath(getLoginRedirectUrl());
  if (fromRedirect) {
    return fromRedirect;
  }
  if (isSupplierUserCode(userCode)) {
    return 'microLsp';
  }
  return 'microDataReport';
};

/**
 * 登录页预首页子应用（唯一会真正 preFetch 首页的入口；同名去重）
 * 换账号且解析结果变化时才会再预
 */
export const prefetchHomeMicroAppOnLoginPage = (userCode?: string | null): string | null => {
  const code = (userCode ?? localStorage.getItem(STORE_ACCOUNT) ?? '').trim() || null;
  const appName = resolveHomePrefetchApp(code);
  if (!(appName in PREFETCHABLE_MICRO_APPS)) {
    return null;
  }
  homePrefetchApp = appName;
  prefetchAppsOnce(buildPrefetchApps([appName]), 0);
  return appName;
};

/**
 * initSystem 成功后：不预首页，只调度其余白名单延迟预加载
 * 强刷新：用当前路径 micro* / 账号类型标记首页并排除
 */
export const prefetchMicroAppsByAuthority = (
  _authority?: any[],
  othersDelay = MICRO_APP_PREFETCH_DELAY_MS,
  userCode?: string | null,
) => {
  if (othersPrefetchScheduled) {
    return;
  }

  const home =
    homePrefetchApp ??
    extractMicroAppNameFromPath(getCurrentHashPath()) ??
    (isSupplierUserCode(userCode ?? cookies.get('apmUser')) ? 'microLsp' : 'microDataReport');

  homePrefetchApp = home;
  // 只标记、不 preFetch 首页（与挂载间隔过短，预加载无作用）
  prefetchedApps.add(home);
  othersPrefetchScheduled = true;

  // 供应商首页为 LSP：不再预其它
  if (home === 'microLsp') {
    return;
  }

  const otherNames = Object.keys(PREFETCHABLE_MICRO_APPS).filter(
    (name) => name !== home && name !== 'microLsp',
  );
  if (otherNames.length === 0) {
    return;
  }
  prefetchAppsOnce(buildPrefetchApps(otherNames), othersDelay);
};

/**
 * 应用启动（main.ts）：仅已登录强刷新时标记当前路径，不预首页
 * 登录页预首页由 pwdLogin 负责，此处不再因 redirect/记住账号再预一轮
 */
export const bootstrapHomeMicroAppPrefetch = () => {
  if (!localStorage.getItem(USER_INFO)) {
    return;
  }
  const fromPath = extractMicroAppNameFromPath(getCurrentHashPath());
  if (fromPath) {
    homePrefetchApp = fromPath;
    prefetchedApps.add(fromPath);
  }
};

/** 登出时清空，避免下次登录沿用旧预取状态 */
export const clearMicroAppPrefetchState = () => {
  prefetchedApps.clear();
  othersPrefetchScheduled = false;
  homePrefetchApp = null;
};
